import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { GitMerge, Loader2, Scan, Split } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import { Field, FieldGroup, FieldLabel } from '@quebon/ui/components/field'
import { Input } from '@quebon/ui/components/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@quebon/ui/components/table'
import { Textarea } from '@quebon/ui/components/textarea'
import { handleApiError } from '@/api/client'
import {
  applyMerge,
  applySplit,
  decideMergeReview,
  fetchMergeReviews,
  previewMerge,
  previewSplit,
  scanForDuplicates,
  type MergeContactProjection,
  type MergePreview,
  type MergeReviewListItem,
  type SplitPreview,
} from '@/api/lcCrm'
import {
  MERGE_REVIEW_REASON_LABEL,
  MERGE_REVIEW_STATUS_LABEL,
  formatKstDateTime,
  formatPhone,
  labelOf,
} from './crmLabels'
import {
  DefinitionRow,
  EmptyRow,
  ErrorBlock,
  FilterSelect,
  LabeledSelect,
  LoadingBlock,
  Pagination,
} from './crmComponents'

const PAGE_SIZE = 20

/**
 * 병합 검토 (설계 §9.2).
 *
 * 승인·거절은 검토 큐를 정리할 뿐 실제 병합을 하지 않는다. 병합은 항상 preview로 무엇이 옮겨지고
 * 무엇이 덮이는지 본 뒤 `previewToken`으로만 실행된다. 토큰은 10분이고 그 사이 대상이 바뀌면
 * `CRM_STALE_VERSION`으로 거절된다.
 */
export function CrmMergePage() {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<string | undefined>('PENDING')
  const [page, setPage] = useState(0)
  const [mergeTarget, setMergeTarget] = useState<{ source: number; target: number } | null>(null)
  const [splitContactId, setSplitContactId] = useState<number | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['lc-crm-merge-reviews', status, page],
    queryFn: () => fetchMergeReviews({ status, page, size: PAGE_SIZE }),
  })

  const scan = useMutation({
    mutationFn: scanForDuplicates,
    onSuccess: (result) => {
      toast.success(
        `연락처 ${result.scannedContacts}건을 훑어 ${result.flaggedReviews}건을 검토로 올렸습니다`,
      )
      queryClient.invalidateQueries({ queryKey: ['lc-crm-merge-reviews'] })
    },
    onError: (err) => toast.error(handleApiError(err) || '재탐지 실패'),
  })

  const decide = useMutation({
    mutationFn: (args: { reviewId: number; approved: boolean; rejectionReason?: string }) =>
      decideMergeReview(args.reviewId, {
        approved: args.approved,
        rejectionReason: args.rejectionReason,
      }),
    onSuccess: () => {
      toast.success('검토 결과를 기록했습니다')
      queryClient.invalidateQueries({ queryKey: ['lc-crm-merge-reviews'] })
    },
    onError: (err) => toast.error(handleApiError(err) || '검토 결정 실패'),
  })

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">병합 검토</h2>
          <p className="text-sm text-muted-foreground">
            같은 사람으로 의심되는 연락처를 확인하고 병합·분리합니다
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" disabled={scan.isPending} onClick={() => scan.mutate()}>
            {scan.isPending ? <Loader2 className="size-4 animate-spin" /> : <Scan className="size-4" />}
            중복 재탐지
          </Button>
          <Button variant="outline" onClick={() => setSplitContactId(0)}>
            <Split className="size-4" />
            연락처 분리
          </Button>
        </div>
      </div>

      <Alert>
        <AlertTitle>승인은 병합이 아닙니다</AlertTitle>
        <AlertDescription>
          검토 승인·거절은 큐를 정리합니다. 실제 병합은 미리보기에서 무엇이 옮겨지는지 확인한 뒤
          실행됩니다.
        </AlertDescription>
      </Alert>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-muted/40 p-3">
        <FilterSelect
          className="w-40"
          label="검토 상태"
          value={status}
          onChange={(value) => {
            setStatus(value)
            setPage(0)
          }}
          items={Object.entries(MERGE_REVIEW_STATUS_LABEL).map(([value, label]) => ({ value, label }))}
        />
      </div>

      {isLoading && <LoadingBlock />}
      {error && <ErrorBlock />}

      {data && (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>검토 ID</TableHead>
                <TableHead>연락처 A</TableHead>
                <TableHead>연락처 B</TableHead>
                <TableHead>사유</TableHead>
                <TableHead>상태</TableHead>
                <TableHead>생성</TableHead>
                <TableHead className="text-right">작업</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.content.length === 0 && (
                <EmptyRow colSpan={7}>
                  <GitMerge className="mx-auto mb-2 size-8 opacity-40" />
                  검토할 중복 후보가 없습니다
                </EmptyRow>
              )}
              {data.content.map((review: MergeReviewListItem) => (
                <TableRow key={review.reviewId}>
                  <TableCell className="tabular-nums">{review.reviewId}</TableCell>
                  <TableCell>
                    {review.contactAName ?? '-'}
                    <span className="ml-1 text-xs text-muted-foreground tabular-nums">
                      #{review.contactAId}
                    </span>
                  </TableCell>
                  <TableCell>
                    {review.contactBName ?? '-'}
                    <span className="ml-1 text-xs text-muted-foreground tabular-nums">
                      #{review.contactBId}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {labelOf(MERGE_REVIEW_REASON_LABEL, review.reviewReason)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={review.status === 'PENDING' ? 'default' : 'secondary'}>
                      {labelOf(MERGE_REVIEW_STATUS_LABEL, review.status)}
                    </Badge>
                  </TableCell>
                  <TableCell className="tabular-nums">{formatKstDateTime(review.createdAt)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setMergeTarget({ source: review.contactBId, target: review.contactAId })
                        }
                      >
                        병합 미리보기
                      </Button>
                      {review.status === 'PENDING' && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={decide.isPending}
                            onClick={() => decide.mutate({ reviewId: review.reviewId, approved: true })}
                          >
                            승인
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={decide.isPending}
                            onClick={() =>
                              decide.mutate({
                                reviewId: review.reviewId,
                                approved: false,
                                rejectionReason: '운영자 판단',
                              })
                            }
                          >
                            거절
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Pagination
            page={page}
            totalPages={data.totalPages}
            totalElements={data.totalElements}
            onChange={setPage}
          />
        </>
      )}

      {mergeTarget && (
        <MergeFlowDialog
          sourceContactId={mergeTarget.source}
          targetContactId={mergeTarget.target}
          onClose={() => setMergeTarget(null)}
          onDone={() => {
            setMergeTarget(null)
            queryClient.invalidateQueries({ queryKey: ['lc-crm-merge-reviews'] })
          }}
        />
      )}

      {splitContactId !== null && (
        <SplitFlowDialog
          onClose={() => setSplitContactId(null)}
          onDone={() => {
            setSplitContactId(null)
            queryClient.invalidateQueries({ queryKey: ['lc-crm-merge-reviews'] })
          }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------- 병합

/** preview → confirm → apply. 토큰 없이는 실행 버튼이 나타나지 않는다. */
function MergeFlowDialog({
  sourceContactId,
  targetContactId,
  onClose,
  onDone,
}: {
  sourceContactId: number
  targetContactId: number
  onClose: () => void
  onDone: () => void
}) {
  const [preview, setPreview] = useState<MergePreview | null>(null)

  const previewMutation = useMutation({
    mutationFn: () => previewMerge(sourceContactId, targetContactId),
    onSuccess: setPreview,
    onError: (err) => toast.error(handleApiError(err) || '병합 미리보기 실패'),
  })

  const applyMutation = useMutation({
    mutationFn: (token: string) => applyMerge(token),
    onSuccess: (result) => {
      toast.success(
        `연락처 #${result.removedContactId}를 #${result.mergedContactId}로 병합했습니다 ` +
          `(접수 ${result.movedIntakeCount}건, 리드 ${result.movedLeadCount}건 이동)`,
      )
      onDone()
    },
    onError: (err) => toast.error(handleApiError(err) || '병합 실행 실패'),
  })

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>연락처 병합</DialogTitle>
          <DialogDescription>
            #{sourceContactId}를 #{targetContactId}로 합칩니다. 되돌리기 어려우니 미리보기를 먼저 봅니다.
          </DialogDescription>
        </DialogHeader>

        {!preview && (
          <div className="flex justify-center py-6">
            <Button disabled={previewMutation.isPending} onClick={() => previewMutation.mutate()}>
              {previewMutation.isPending && <Loader2 className="size-4 animate-spin" />}
              미리보기 불러오기
            </Button>
          </div>
        )}

        {preview && (
          <div className="flex flex-col gap-4">
            {preview.activeLeadConflict && (
              <Alert variant="destructive">
                <AlertTitle>두 연락처 모두 진행 중 리드가 있습니다</AlertTitle>
                <AlertDescription>
                  합치면 한쪽 파이프라인이 가려집니다. 정말 같은 사람인지 다시 확인해 주세요.
                </AlertDescription>
              </Alert>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <ContactProjectionCard title="병합 전 (대상)" projection={preview.before} />
              <ContactProjectionCard title="병합 후" projection={preview.after} />
            </div>

            <dl className="rounded-lg border border-border p-3">
              <DefinitionRow label="옮겨질 접수">
                <span className="tabular-nums">{preview.movingIntakeIds.length}건</span>
              </DefinitionRow>
              <DefinitionRow label="옮겨질 리드">
                <span className="tabular-nums">{preview.movingLeadIds.length}건</span>
              </DefinitionRow>
              <DefinitionRow label="옮겨질 상담">
                <span className="tabular-nums">{preview.movingConsultationCount}건</span>
              </DefinitionRow>
              <DefinitionRow label="옮겨질 할 일">
                <span className="tabular-nums">{preview.movingTaskCount}건</span>
              </DefinitionRow>
              <DefinitionRow label="미리보기 만료">{formatKstDateTime(preview.expiresAt)}</DefinitionRow>
            </dl>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>취소</Button>
          {preview && (
            <Button
              variant="destructive"
              disabled={applyMutation.isPending}
              onClick={() => applyMutation.mutate(preview.previewToken)}
            >
              {applyMutation.isPending && <Loader2 className="size-4 animate-spin" />}
              병합 실행
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ContactProjectionCard({
  title,
  projection,
}: {
  title: string
  projection: MergeContactProjection
}) {
  return (
    <div className="rounded-lg border border-border p-3">
      <h3 className="mb-2 text-sm font-medium text-foreground">{title}</h3>
      <dl>
        <DefinitionRow label="ID">
          <span className="tabular-nums">{projection.contactId}</span>
        </DefinitionRow>
        <DefinitionRow label="이름">{projection.canonicalName}</DefinitionRow>
        <DefinitionRow label="연락처">
          <span className="tabular-nums">{formatPhone(projection.phoneNormalized)}</span>
        </DefinitionRow>
        <DefinitionRow label="지역">
          {[projection.sido, projection.sigungu].filter(Boolean).join(' ') || '-'}
        </DefinitionRow>
        <DefinitionRow label="접수 · 리드">
          <span className="tabular-nums">
            {projection.intakeCount} · {projection.leadCount}
          </span>
        </DefinitionRow>
      </dl>
    </div>
  )
}

// ---------------------------------------------------------------- 분리

/**
 * 분리는 "이 연락처의 이 접수들은 다른 사람 것"이라고 선언하는 작업이다.
 * 병합과 같이 preview → apply를 거친다.
 *
 * 같은 번호를 쓰는 두 사람이 생기는 경우는 둘인데 조치가 다르다. 가족이 함께 쓰면 번호의
 * 대표는 원본이 계속 갖고, 번호를 물려받았으면 대표가 새 연락처로 넘어간다. 앞으로 이 번호로
 * 들어오는 접수가 누구에게 붙느냐가 갈리므로 실행 전에 물어본다.
 */
function SplitFlowDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [contactId, setContactId] = useState('')
  const [intakeIds, setIntakeIds] = useState('')
  const [phoneOwner, setPhoneOwner] = useState('keep')
  const [preview, setPreview] = useState<SplitPreview | null>(null)
  const transferPhoneIdentity = phoneOwner === 'transfer'

  const parsedIntakeIds = intakeIds
    .split(/[\s,]+/)
    .map((value) => Number(value.trim()))
    .filter((value) => Number.isFinite(value) && value > 0)

  const previewMutation = useMutation({
    mutationFn: () => previewSplit(Number(contactId), parsedIntakeIds, transferPhoneIdentity),
    onSuccess: setPreview,
    onError: (err) => toast.error(handleApiError(err) || '분리 미리보기 실패'),
  })

  const applyMutation = useMutation({
    mutationFn: (token: string) => applySplit(token),
    onSuccess: (result) => {
      toast.success(
        transferPhoneIdentity
          ? `연락처 #${result.newContactId}로 접수 ${result.movedIntakeCount}건과 번호를 넘겼습니다`
          : `연락처 #${result.newContactId}로 접수 ${result.movedIntakeCount}건을 분리했습니다`,
      )
      onDone()
    },
    onError: (err) => toast.error(handleApiError(err) || '분리 실행 실패'),
  })

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>연락처 분리</DialogTitle>
          <DialogDescription>
            한 연락처에 잘못 묶인 접수를 새 연락처로 떼어냅니다.
          </DialogDescription>
        </DialogHeader>

        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="split-contact">연락처 ID</FieldLabel>
            <Input
              id="split-contact"
              type="number"
              min={1}
              value={contactId}
              onChange={(e) => {
                setContactId(e.target.value)
                setPreview(null)
              }}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="split-intakes">떼어낼 접수 ID</FieldLabel>
            <Textarea
              id="split-intakes"
              rows={2}
              placeholder="쉼표 또는 공백으로 구분"
              value={intakeIds}
              onChange={(e) => {
                setIntakeIds(e.target.value)
                setPreview(null)
              }}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="split-phone-owner">이 번호는 앞으로 누구 것입니까</FieldLabel>
            <LabeledSelect
              id="split-phone-owner"
              ariaLabel="번호 대표 선택"
              value={phoneOwner}
              onChange={(next) => {
                setPhoneOwner(next)
                setPreview(null)
              }}
              items={[
                { value: 'keep', label: '원래 연락처 (가족이 함께 쓴다)' },
                { value: 'transfer', label: '새 연락처 (번호를 물려받았다)' },
              ]}
            />
          </Field>
        </FieldGroup>

        {preview && (
          <dl className="rounded-lg border border-border p-3">
            <DefinitionRow label="새 연락처 이름">{preview.newContactName}</DefinitionRow>
            <DefinitionRow label="남는 접수">
              <span className="tabular-nums">{preview.stayingIntakeIds.length}건</span>
            </DefinitionRow>
            <DefinitionRow label="떼어낼 접수">
              <span className="tabular-nums">{preview.movingIntakeIds.length}건</span>
            </DefinitionRow>
            <DefinitionRow label="앞으로 이 번호의 접수">
              {preview.transfersPhoneIdentity
                ? '새 연락처에 붙습니다 (원래 연락처는 자기 이력을 그대로 갖고 비활성이 됩니다)'
                : '원래 연락처에 붙습니다'}
            </DefinitionRow>
            <DefinitionRow label="미리보기 만료">{formatKstDateTime(preview.expiresAt)}</DefinitionRow>
          </dl>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>취소</Button>
          {preview ? (
            <Button
              variant="destructive"
              disabled={applyMutation.isPending}
              onClick={() => applyMutation.mutate(preview.previewToken)}
            >
              {applyMutation.isPending && <Loader2 className="size-4 animate-spin" />}
              분리 실행
            </Button>
          ) : (
            <Button
              disabled={!contactId || parsedIntakeIds.length === 0 || previewMutation.isPending}
              onClick={() => previewMutation.mutate()}
            >
              {previewMutation.isPending && <Loader2 className="size-4 animate-spin" />}
              미리보기
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
